import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { TextField } from '@mui/material';
import Stack from '@mui/material/Stack';
import { useCreateInvoiceItem } from '../../../hooks/invoice/useCreateInvoiceItem';
import { useUpdateInvoiceItem } from '../../../hooks/invoice/useUpdateInvoiceItem';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';

export default function InvoiceItemForm({ onInvoiceItemSaveSuccess = null, invoice = null, item = null, mode = "create", open, onClose }) {

    const { t } = useTranslation('common'); 

    const [name, setName] = React.useState(item ? item.name : "");
    const [unitPrice, setUnitPrice] = React.useState(item ? item.unit_price : "");
    const [quantity, setQuantity] = React.useState(item ? item.quantity : "");

    const { create } = useCreateInvoiceItem(onInvoiceItemSaveSuccess);
    const { update } = useUpdateInvoiceItem(onInvoiceItemSaveSuccess);

    const { showSnackbar } = useSnackbar();

    React.useEffect(() => {
        // reset field when there are changes (e.g. when opening edit)
        setName(item ? item.name : "");
        setUnitPrice(item ? item.unit_price : "");
        setQuantity(item ? item.quantity : "");
    }, [item, open]);


    const handleSave = async () => {
        const isValidCurrency = (value) => {
            // Matches numbers like "1", "1.23", ".9", ".50"
            return /^(?:\d+|\d*\.\d{1,2})$/.test(value);
        };

        if (!name.trim()) {
            showSnackbar(t('invoice.items.messages.name_required'), "warning");
            return;
        }
        if (!unitPrice.trim()) {
            showSnackbar(t('invoice.items.messages.unit_price_required'), "warning");
            return;
        }
        if (!isValidCurrency(unitPrice)) {
            showSnackbar(t('invoice.items.messages.unit_price_invalid'), "warning");
            return;
        }
        if (!quantity.trim()) {
            showSnackbar(t('invoice.items.messages.quantity_required'), "warning");
            return;
        }
        if (!isValidCurrency(quantity)) {
            showSnackbar(t('invoice.items.messages.quantity_invalid'), "warning");
            return;
        }

        const payload = {
            invoice_id: invoice.id,
            name: name,
            unit_price: Number(unitPrice),
            quantity: Number(quantity)
        };

        try {
            if (mode === "create") {
              await create(payload);
              showSnackbar(t('invoice.items.messages.create_success'), "success");
              onClose();
            } else if (mode === "edit" && item) {
                await update(item.id, payload);
                showSnackbar(t('invoice.items.messages.update_success'), "success");
                onClose();
            }
            setName("");
            setUnitPrice("");
            setQuantity("");
            onClose();     // close dialog
        } catch (err) {
            console.log("err.response", err.response);
            showSnackbar(t('invoice.items.messages.save_fail'), "error");
        }
    };

    const handleCancel = () => {
        setName("");
        setUnitPrice("");
        setQuantity("");
        onClose();
    };

    return (
        <>
        <Dialog onClose={handleCancel} open={open} disableRestoreFocus>

        <DialogTitle>{mode === "create" ? "Add Item" : "Edit Item"}</DialogTitle>

        <DialogContent dividers>
            <Stack spacing={1}>
            <TextField
                label="Item Name"
                variant="outlined"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
            />
            <TextField
                label="Unit Price"
                variant="outlined"
                value={unitPrice}
                onChange={(e) => setUnitPrice(e.target.value)}
                slotProps={{
                    input: {
                        type: 'number',
                        inputMode: 'decimal',
                        pattern: '^[0-9]+(\\.[0-9]{0,2})?$',
                    },
                }}
                fullWidth
            />
            <TextField
                label="Quantity"
                variant="outlined"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                slotProps={{
                    input: {
                        type: 'number',
                        inputMode: 'decimal',
                        pattern: '^[0-9]+(\\.[0-9]{0,2})?$',
                    },
                }}
                fullWidth
            />
            </Stack>
        </DialogContent>

        <DialogActions>
            <Button onClick={handleSave}>
                Save
            </Button>
            <Button onClick={handleCancel}>Cancel</Button>
        </DialogActions>
        </Dialog>
        </>
    );
}